import { type ReactNode, useMemo, useState } from 'react';
import { type CaptureContext, type CaptureResult, type CaptureToken, PRIORITIES, fmtRelativeDay, fmtShort, isNearDay, localToday, parseCapture } from '@quadro/domain';
import { Avatar } from '../../components/Avatar';
import { Icon } from '../../components/Icon';
import { useWorkspace } from '../../state/workspace';
import { useEntities } from './useMap';

export function useCaptureContext(): CaptureContext {
  const ws = useWorkspace();
  const ents = useEntities();
  return useMemo(() => ({
    today: localToday(),
    projects: ws.settings.projects,
    themes: ws.settings.themes,
    people: ws.members.map(p => ({ id: p.id, name: p.name })),
    entities: ents.list.map(n => ({ id: n.id, name: n.t, short: n.curto })),
  }), [ws, ents.list]);
}

export function TokenChip({ token }: { token: CaptureToken }) {
  const ws = useWorkspace();
  const today = localToday();
  switch (token.kind) {
    case 'project': return <span className="pchip" style={{ '--pc': ws.settings.projects.find(p => p.id === token.id)?.color } as React.CSSProperties}><i />{token.label}</span>;
    case 'theme': return <span className="pchip" style={{ '--pc': ws.settings.themes.find(p => p.id === token.id)?.color } as React.CSSProperties}><i />{token.label}</span>;
    case 'person': return <span className="pchip"><Avatar id={token.id} />{token.label}</span>;
    case 'entity': return <span className="pchip"><Icon name="ent" />{token.label}</span>;
    case 'priority': return <span className="pchip">Prioridade {PRIORITIES[token.value].toLowerCase()}</span>;
    case 'due': return <span className="pchip">Prazo {fmtRelativeDay(token.date, today)}{isNearDay(token.date, today) ? ` (${fmtShort(token.date)})` : ''}</span>;
  }
}

/**
 * Campo de captura em linguagem natural com prévia dos marcadores reconhecidos.
 * `onSubmit` devolve true para limpar o campo.
 */
export function CaptureField({ id, placeholder, label, onSubmit, onEscape, variant = 'write', autoFocus, hint, prefix }: {
  id?: string; placeholder: string; label: string; variant?: 'write' | 'add'; autoFocus?: boolean; hint?: string; prefix?: ReactNode;
  onSubmit: (r: CaptureResult, raw: string) => boolean | Promise<boolean>;
  onEscape?: (value: string) => void;
}) {
  const ctx = useCaptureContext();
  const [value, setValue] = useState('');
  const [focused, setFocused] = useState(false);
  const result = useMemo(() => parseCapture(value, ctx), [value, ctx]);
  const chips = value.trim()
    ? result.tokens.map((t, i) => <TokenChip key={i} token={t} />)
    : hint ? <span className="pchip">{hint}</span> : null;
  const input = (
    <input id={id} value={value} placeholder={placeholder} aria-label={label} autoComplete="off" autoFocus={autoFocus}
      onChange={e => setValue(e.target.value)} onFocus={() => setFocused(true)} onBlur={() => { setFocused(false); onEscape && !value.trim() && variant === 'add' && onEscape(''); }}
      onKeyDown={async e => {
        if (e.key === 'Enter' && value.trim()) { e.preventDefault(); if (await onSubmit(result, value)) setValue(''); }
        if (e.key === 'Escape') { setValue(''); (e.target as HTMLInputElement).blur(); onEscape?.(''); }
      }} />
  );
  if (variant === 'add') return <div className="add-in">{input}<div className="chips">{value.trim() ? chips : null}</div></div>;
  return (
    <div className="write">
      <span className="write-plus">{prefix ?? <Icon name="plus" />}</span>
      {input}
      <span className={`write-chips ${focused ? 'show' : ''}`}>{chips}</span>
    </div>
  );
}
